import { useEffect, useRef } from "react";
import { dayTabLabel } from "../lib/days.ts";
import { tabKeys, tabStop } from "../ui/tabs.ts";

export type MapDay = {
  /** `YYYY-MM-DD`, local. */
  day: string;
  /** Where the dial should land when this day is picked. */
  at: number;
  /** How many recordings that day holds. */
  count: number;
};

/**
 * The trip's days along the top of the map, so a long trip is reachable without turning the dial
 * through all of it. The rim says the time of day and cannot say which day it is; this can, and
 * it is also the only way to cross a day boundary in one gesture.
 *
 * It scrolls sideways, because a two-week trip does not fit a phone, and the day in view is kept
 * in view as the dial is turned past midnight. A one-day trip has nothing to switch between, so
 * the page does not render it at all.
 */
export function MapDays({
  days,
  current,
  onPick,
  className = "map-days",
}: {
  days: MapDay[];
  /** The local day the playhead is in. */
  current: string;
  onPick: (at: number) => void;
  /**
   * Which strip this is. The default floats over the phone's map; the editor's timeline passes
   * its own, because there the days sit in the page under the map rather than on top of it.
   * The behaviour — the tablist, the counts, keeping the current day in view — is the same, and
   * is the reason this is one component and not two.
   */
  className?: string;
}) {
  const strip = useRef<HTMLDivElement>(null);
  const at = days.findIndex((d) => d.day === current);

  // Turning the dial past midnight changes the day; the strip follows so the current one is never
  // off-screen. `nearest` so it only moves when it has to.
  useEffect(() => {
    const on = strip.current?.querySelector<HTMLElement>("[data-on='true']");
    // Optional on purpose: not every environment this renders in implements scrolling, and a
    // tab that cannot be scrolled into view is not a reason to take the map down.
    on?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [current]);

  return (
    <div className={className} ref={strip} role="tablist" aria-label="Days" onKeyDown={tabKeys}>
      {days.map((d, i) => (
        <button
          key={d.day}
          type="button"
          role="tab"
          aria-selected={d.day === current}
          tabIndex={tabStop(i, at)}
          data-on={d.day === current}
          className={`day-tab${d.day === current ? " on" : ""}`}
          onClick={() => onPick(d.at)}
        >
          <span className="day-tab-label">{dayTabLabel(d.day)}</span>
          <span className="day-tab-count">
            {d.count === 0 ? "—" : d.count}
            <span className="sr">{d.count === 1 ? " recording" : " recordings"}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
